import { useState } from 'react';
import { endpoints } from '../api/client.js';
import { Panel } from './Panel.jsx';
import { Tag, TlsBadge } from './StatusPill.jsx';

export default function CustomDomains({ siteName, domains = [], publicIp, isAdmin, onChanged }) {
  const [domain, setDomain] = useState('');
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  async function run(key, action, success) {
    setPending(key);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
      onChanged();
      return true;
    } catch (err) {
      setError(err?.message || 'Domain update failed.');
      return false;
    } finally {
      setPending(null);
    }
  }

  function add(event) {
    event.preventDefault();
    const value = domain.trim();
    if (!value) return;
    run('add', () => endpoints.addDomain(siteName, value, 'live'), `${value} was added to live.`)
      .then((ok) => { if (ok) setDomain(''); });
  }

  return (
    <Panel title="Custom domains" subtitle="Customer hostnames always attach to live" id="domains">
      {isAdmin ? (
        <form className="domain-add" onSubmit={add}>
          <label className="field" htmlFor="domain-name">
            <span className="field__label">Domain</span>
            <input
              id="domain-name"
              className="field__input mono"
              value={domain}
              onChange={(event) => setDomain(event.target.value)}
              placeholder="www.example.com"
              disabled={Boolean(pending)}
            />
          </label>
          <button type="submit" className="btn" disabled={Boolean(pending) || !domain.trim()}>
            {pending === 'add' ? 'Adding…' : 'Add to live'}
          </button>
        </form>
      ) : null}

      {publicIp ? (
        <p className="panel__note">Point an A record for the domain at <span className="mono">{publicIp}</span>.</p>
      ) : null}
      {error ? <p className="notice notice--error" role="alert">{error}</p> : null}
      {notice ? <p className="notice" role="status">{notice}</p> : null}

      {domains.length === 0 ? (
        <p className="state__body">No live domains are configured.</p>
      ) : (
        <ul className="rowlist domain-actions">
          {domains.map((entry) => (
            <li className="rowlist__item" key={entry.domain}>
              <span className="rowlist__main">
                <span className="mono">{entry.domain}</span>
                {entry.managed ? <Tag>platform</Tag> : null}
              </span>
              <span className="domain-actions__buttons">
                <TlsBadge secured={Boolean(entry.tls)} />
                {isAdmin && !entry.tls ? (
                  <button
                    type="button"
                    className="btn btn--quiet"
                    disabled={Boolean(pending)}
                    onClick={() => run(
                      `cert-${entry.domain}`,
                      () => endpoints.issueCertificate(siteName, entry.domain),
                      `Certificate issued for ${entry.domain}.`,
                    )}
                  >
                    {pending === `cert-${entry.domain}` ? 'Issuing…' : 'Issue certificate'}
                  </button>
                ) : null}
                {isAdmin && !entry.managed ? (
                  <button
                    type="button"
                    className="btn btn--danger-quiet"
                    disabled={Boolean(pending)}
                    onClick={() => run(
                      `remove-${entry.domain}`,
                      () => endpoints.removeDomain(siteName, entry.domain),
                      `${entry.domain} was removed.`,
                    )}
                  >
                    {pending === `remove-${entry.domain}` ? 'Removing…' : 'Remove'}
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
